/**
 * MFA Verification Component
 * SOC 2 Controls: CC6.1, CC6.2
 * ISO 27001: A.9.4.2
 *
 * Handles MFA verification during login for users with MFA enabled.
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2, ArrowLeft } from 'lucide-react';

interface MFAVerificationProps {
  onVerified: () => void;
  onCancel: () => void;
}

export default function MFAVerification({ onVerified, onCancel }: MFAVerificationProps) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get the user's MFA factor on mount
  useEffect(() => {
    const getFactors = async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) {
        setError('Failed to load MFA factors');
        return;
      }

      // Get the first verified TOTP factor
      const totpFactor = data.totp.find((f) => f.status === 'verified');
      if (totpFactor) {
        setFactorId(totpFactor.id);
      } else {
        setError('No MFA factor found');
      }
    };

    getFactors();
  }, []);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleVerify = async () => {
    if (!factorId) {
      setError('MFA factor not found');
      return;
    }

    if (code.length !== 6 && !useBackupCode) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create a challenge
      const { data: challengeData, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId });

      if (challengeError) throw challengeError;

      // Verify the code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: code.trim(),
      });

      if (verifyError) throw verifyError;

      onVerified();
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('Invalid')) {
          setError('Invalid code. Please try again.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Verification failed. Please try again.');
      }
      setCode('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && code.length === 6) {
      handleVerify();
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <CardTitle>Two-Factor Authentication</CardTitle>
        <CardDescription>
          {useBackupCode
            ? 'Enter one of your backup codes'
            : 'Enter the 6-digit code from your authenticator app'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="mfa-code">
            {useBackupCode ? 'Backup Code' : 'Authentication Code'}
          </Label>
          <Input
            ref={inputRef}
            id="mfa-code"
            type="text"
            inputMode={useBackupCode ? 'text' : 'numeric'}
            pattern={useBackupCode ? undefined : '[0-9]*'}
            maxLength={useBackupCode ? 8 : 6}
            placeholder={useBackupCode ? 'XXXXXX' : '000000'}
            value={code}
            onChange={(e) => {
              const value = useBackupCode
                ? e.target.value.toUpperCase()
                : e.target.value.replace(/\D/g, '');
              setCode(value);
            }}
            onKeyDown={handleKeyDown}
            className="text-center text-2xl tracking-widest font-mono"
            autoComplete="one-time-code"
          />
        </div>

        <Button
          onClick={handleVerify}
          disabled={loading || (code.length !== 6 && !useBackupCode)}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify'
          )}
        </Button>

        <div className="flex items-center justify-between text-sm">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to login
          </Button>

          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setCode('');
              setError(null);
            }}
          >
            {useBackupCode ? 'Use authenticator app' : 'Use backup code'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
